/* ============================================================
   Cloudinary URL builder
   ----------------------------------------------------------
   Client-safe. Uses ONLY the public cloud_name. The API secret
   stays on the server / in seed scripts.

   Usage:
     import { cld, cldVideo, cldSrcSet } from "@/lib/cloudinary";
     const url = cld("adel/hero-employer", { w: 1920, q: "auto" });
   ============================================================ */

const CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || "dxd0lmhdr";

const BASE_IMG = `https://res.cloudinary.com/${CLOUD_NAME}/image/upload`;
const BASE_VID = `https://res.cloudinary.com/${CLOUD_NAME}/video/upload`;

/* ------------------------------------------------------------
   adel/v2/* library — delivery versions.
   These assets only deliver on VERSIONED URLs; Cloudinary returns
   400 for unversioned delivery of them (versioned URLs are served
   immutable, max-age 30d — the better path anyway). `cld()` injects
   the right version automatically, so call sites just use
   cld("adel/v2/NAME", {...}) with no version awareness.
   Stable while the image bytes are unchanged. To regenerate after
   re-uploading assets, re-run the Admin API listing in
   scratchpad/upload_v2.sh and paste the values here.
   ------------------------------------------------------------ */
const V2_VER = {
  "employer-hero": 1784733360,
  "jobseeker-hero": 1784733358,
  "nairobi-skyline": 1784733363,
  "journey-consult": 1784733374,
  "journey-travel": 1784733377,
  "journey-arrival": 1784733685,
  "story-1": 1784735888,
  "story-2": 1784735891,
  "story-3": 1784735895,
  /* Employer "How it works" step tiles (generated 2026-07-23; outcome-only, no method-revealing imagery). */
  "employer-step-brief": 1784812588,
  "employer-step-source": 1784812593,
  "employer-step-interview": 1784812597,
  "employer-step-travel": 1784812601,
  "sector-construction-infrastructure": 1784750053,
  "sector-hospitality-catering": 1784733367,
  "sector-healthcare-caregiving": 1784733369,
  "sector-logistics-warehousing": 1784733372,
  "sector-transportation": 1784733696,
  "sector-manufacturing-industrial": 1784733698,
  "sector-facilities-management": 1784733701,
  "sector-security-services": 1784735875,
};

function versionSegment(publicId, opts) {
  if (opts && opts.v) return `/v${opts.v}`;
  const m = /^adel\/v2\/(.+)$/.exec(publicId);
  const ver = m && V2_VER[m[1]];
  return ver ? `/v${ver}` : "";
}

function txString(opts = {}) {
  const {
    w, h, q = "auto", f = "auto",
    g = null, c = null, ar = null,
    e = null, /* effects */
    dpr = null,
  } = opts;
  const parts = [];
  if (w) parts.push(`w_${w}`);
  if (h) parts.push(`h_${h}`);
  if (c) parts.push(`c_${c}`);
  if (g) parts.push(`g_${g}`);
  if (ar) parts.push(`ar_${ar}`);
  if (e) parts.push(`e_${e}`);
  if (dpr) parts.push(`dpr_${dpr}`);
  parts.push(`q_${q}`);
  parts.push(`f_${f}`);
  return parts.join(",");
}

export function cld(publicId, opts = {}) {
  /* dpr_2 keeps section imagery crisp on retina phones (sources are ~1.4k). */
  return `${BASE_IMG}/${txString({ dpr: 2, ...opts })}${versionSegment(publicId, opts)}/${publicId}`;
}

export function cldVideo(publicId, opts = {}) {
  /* For video, q_auto + f_auto deliver AV1/WebM/MP4 by capability */
  return `${BASE_VID}/${txString({ q: "auto", f: "auto", ...opts })}/${publicId}`;
}

/* Build a responsive srcset for an <img sizes=... srcset=...> attribute */
export function cldSrcSet(publicId, opts = {}, widths = [640, 960, 1280, 1600, 1920]) {
  return widths
    .map((w) => `${cld(publicId, { ...opts, w })} ${w}w`)
    .join(", ");
}

/* Convenience: get a responsive image config (src + srcset + sizes) */
export function cldImg(publicId, opts = {}, widths) {
  return {
    src:    cld(publicId, { ...opts, w: 1280 }),
    srcSet: cldSrcSet(publicId, opts, widths),
  };
}
